<template>
  <div class="custom-date">
    <n-time-picker
      v-if="isTime"
      v-bind="props"
      clearable
      :bordered="bordered"
      :value="internalValue"
      :format="format || 'HH:mm:ss'"
      @update:value="handleUpdate"
      @clear="handleClear"
      :disabled="disabled"
    />
    <n-date-picker
      v-else
      :value="internalValue"
      :format="format || 'yyyy-MM-dd'"
      :value-format="valueFormat || 'yyyy-MM-dd'"
      :type="dateType || 'date'"
      clearable
      :disabled="disabled"
      @update:value="handleUpdate"
      :bordered="bordered"
      @clear="handleClear"
    />
  </div>
</template>

<script setup lang="ts">
import { format as formatDate, isValid, parse } from 'date-fns'
import { computed } from 'vue'
interface SelectFieldEmits {
  (
    e: 'update:modelValue',
    value: string | number | boolean | (string | number | boolean)[] | undefined
  ): void
}
const props = defineProps<{
  modelValue: string | Date | null | undefined
  size?: 'small' | 'medium' | 'large'
  valueFormat?: string
  format?: string
  type: string
  isTime?: boolean
  isDate?: boolean
  dateType?: string
  bordered?: boolean
  disabled?: boolean
}>()
const emit = defineEmits<SelectFieldEmits>()

// 获取对应的格式化字符串
const getFormatPattern = (format: string) => {
  const formatMap: { [key: string]: string } = {
    'YYYY-MM-dd': 'yyyy-MM-dd',
    'YYYY-MM-DD': 'yyyy-MM-dd',
    'yyyy-MM-dd': 'yyyy-MM-dd',
    'YYYY/MM/dd': 'yyyy/MM/dd',
    'HH:mm:ss': 'HH:mm:ss',
    'HH:mm': 'HH:mm',
    'hh:mm a': 'hh:mm a',
    'yyyy-MM-dd HH:mm:ss': 'yyyy-MM-dd HH:mm:ss'
  }
  return formatMap[format] || format
}

// 将字符串转换为Date 对象
const internalValue = computed(() => {
  if (!props.modelValue) return null
  try {
    if (typeof props.modelValue === 'number') {
      return props.modelValue
    }
    if (props.modelValue instanceof Date) {
      return props.modelValue.getTime()
    }
    const valueFormat =
      props.valueFormat || (props.isTime ? 'HH:mm:ss' : 'yyyy-MM-dd')
    const formatPattern = getFormatPattern(valueFormat)
    const parsedDate = parse(
      props.modelValue as string,
      formatPattern,
      new Date()
    )
    if (isValid(parsedDate)) {
      return parsedDate.getTime()
    }

    // 尝试直接解析
    const date = new Date(props.modelValue as string)
    if (isValid(date)) {
      return date.getTime()
    }

    return null
  } catch (error) {
    console.warn('Date parsing failed:', error)
    return null
  }
})

const handleUpdate = (value: number | [number, number] | null) => {
  if (!value) {
    emit('update:modelValue', '')
    return
  }
  try {
    const valueFormat =
      props.valueFormat || (props.isTime ? 'HH:mm:ss' : 'YYYY-MM-dd')
    const formatPattern = getFormatPattern(valueFormat)

    let formattedValue = ''

    if (Array.isArray(value)) {
      // 日期范围
      const startDate = new Date(value[0])
      const endDate = new Date(value[1])

      const startStr = formatDate(startDate, formatPattern)
      const endStr = formatDate(endDate, formatPattern)
      formattedValue = `${startStr} ~ ${endStr}`
    } else {
      // 单个日期/时间
      const date = new Date(value)
      formattedValue = formatDate(date, formatPattern)
    }

    emit('update:modelValue', formattedValue)
  } catch (error) {
    console.warn('Date formatting failed:', error)
    emit('update:modelValue', '')
  }
}
const handleClear = () => {
  emit('update:modelValue', '')
}
</script>
<style lang="scss" scoped>
.custom-date {
  width: 100%;
  border: 0;
}
</style>
